// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
import uuid from 'uuid';

import { GapCursorSelection, Side } from '@atlaskit/editor-common/selection';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import type { RemixThinkingPluginMeta } from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import { remixThinkingPluginKey } from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import type { RemixModalMetadata } from '../types';

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
	return uuid();
};

export const makeStartRemixThinking =
	(api: ExtractInjectionAPI<MalleableUiPlugin> | undefined) =>
	({
		contentType,
		dynamicUiSubtypeForUpdate,
		isUpdate,
		historyEntryForUpdate,
		remixModalMetadata,
		suppressThinkingHighlight,
	}: {
		contentType?: string;
		dynamicUiSubtypeForUpdate?: string;
		historyEntryForUpdate?: RemixHistoryEntryForUpdate;
		isUpdate?: boolean;
		remixModalMetadata?: RemixModalMetadata;
		suppressThinkingHighlight?: boolean;
	} = {}): EditorCommand =>
	({ tr }) => {
		const selection = tr.selection;
		const isNodeSelection = selection instanceof NodeSelection;
		const isCellSelection = selection instanceof CellSelection;

		const thinkingMeta: RemixThinkingPluginMeta = {
			contentType,
			remixAttemptId: randomId(),
			isThinking: true,
			isUpdate,
			historyEntryForUpdate,
			dynamicUiSubtypeForUpdate,
			thinkingSelectionFrom: selection.from,
			thinkingSelectionTo: isNodeSelection
				? selection.from + selection.node.nodeSize
				: selection.to,
			isNodeSelection: isNodeSelection,
			suppressThinkingHighlight,
		};

		if (isCellSelection) {
			thinkingMeta.cellSelectionRanges = selection.ranges.map((range) => ({
				from: range.$from.pos,
				to: range.$to.pos,
			}));
			thinkingMeta.anchorCellPos = selection.$anchorCell.pos;
			thinkingMeta.headCellPos = selection.$headCell.pos;
		}

		// Clear block selection decorations before applying the thinking state.
		api?.selection?.commands?.clearBlockSelection()({ tr });

		tr.setMeta(remixThinkingPluginKey, thinkingMeta);

		/**
		 * Added this check so that when a userPrompt is pre-supplied in metadata (e.g. from the AI Image Generation flow),
		 * we can pass in necessary metadata to the malleable-ui plugin.
		 * like the userPrompt
		 */
		if (
			remixModalMetadata &&
			remixModalMetadata.userPrompt &&
			((remixModalMetadata.remixSource === 'suggestedRemix' &&
				UNSAFE_expValNoExposure(
					'platform_editor_ai_content_suggested_edits',
					'isEnabled',
					false,
				)) ||
				expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true))
		) {
			tr.setMeta(malleableUiPluginKey, {
				isRemixModalOpen: false,
				remixModalMetadata,
				remixSource: remixModalMetadata.remixSource,
			});
		} else {
			tr.setMeta(malleableUiPluginKey, { isRemixModalOpen: false });
		}

		// Move the active selection away from the selected node so the user can
		// continue editing while the remix is processing.
		const endPos = isNodeSelection ? selection.from + selection.node.nodeSize : selection.to;

		if (isNodeSelection && fg('platform_editor_ai_remix_toolbar')) {
			const $endPos = tr.doc.resolve(endPos);

			tr.setSelection(
				GapCursorSelection.valid($endPos)
					? new GapCursorSelection($endPos, Side.RIGHT)
					: TextSelection.create(tr.doc, endPos),
			);
		} else {
			tr.setSelection(TextSelection.create(tr.doc, endPos));
		}

		return tr;
	};

export function onStartRemixThinking({
	api,
	options,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	options?: {
		contentType?: string;
		dynamicUiSubtypeForUpdate?: string;
		historyEntryForUpdate?: RemixHistoryEntryForUpdate;
		isUpdate?: boolean;
		suppressThinkingHighlight?: boolean;
	};
}): void {
	api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
	api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
}
